/**
 * Activity data (server module `telemetry`): the continuous timeline, tracks, trips and
 * detections. TanStack Query hooks over the typed client; other modules may use these hooks.
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api/client';
import type { components } from '@/lib/api/schema';

type S = components['schemas'];
export type Detection = S['DetectionOut'];
export type Trip = S['TripOut'];
export type TripDetail = S['TripDetailOut'];
export type Segment = S['SegmentOut'];
export type Lane = S['LaneOut'];
export type Lanes = S['LanesOut'];
export type TimelineData = S['TimelineOut'];
export type Track = S['TrackOut'];
export type DetectionSummary = S['DetectionSummary'];
export type ScoreNode = S['ScoreNodeOut'];
export type ActionOut = S['ActionOut'];

export const activityKeys = {
  all: ['activity'] as const,
  lanes: (p: object) => ['activity', 'lanes', p] as const,
  timeline: (p: object) => ['activity', 'timeline', p] as const,
  track: (p: object) => ['activity', 'track', p] as const,
  trips: (p: object) => ['activity', 'trips', p] as const,
  trip: (id: string) => ['activity', 'trip', id] as const,
  detections: (p: object) => ['activity', 'detections', p] as const,
  detection: (id: string) => ['activity', 'detection', id] as const,
  summary: (p: object) => ['activity', 'summary', p] as const,
  scoreTree: ['activity', 'score-tree'] as const,
};

export interface Window {
  start: string;
  end: string;
}

export interface LanesQuery extends Window {
  project?: string[];
  group?: string[];
  vehicle?: string[];
  driver?: string;
}

/** The fleet timeline, a page of vehicles at a time. */
export function useLanes(p: LanesQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: activityKeys.lanes(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/timeline/lanes', { params: { query: { ...p, cursor: pageParam, limit: 50 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** One vehicle's timeline (segments, detections, trips). */
export function useTimeline(vehicle: string | undefined, w: Window) {
  return useQuery({
    queryKey: activityKeys.timeline({ vehicle, ...w }),
    queryFn: () => unwrap(api.GET('/api/timeline', { params: { query: { vehicle: vehicle ?? '', ...w } } })),
    placeholderData: keepPreviousData,
    enabled: !!vehicle,
  });
}

/** Positions and speeds for a map and a speed trace. */
export function useTrack(vehicle: string | undefined, w: Partial<Window>, maxPoints = 2000) {
  return useQuery({
    queryKey: activityKeys.track({ vehicle, ...w, maxPoints }),
    queryFn: () => unwrap(api.GET('/api/track', { params: { query: { vehicle: vehicle ?? '', start: w.start, end: w.end, max_points: maxPoints } } })),
    enabled: !!vehicle && !!w.start && !!w.end,
    staleTime: 60_000,
  });
}

export interface TripsQuery extends Window {
  project?: string[];
  group?: string[];
  vehicle?: string;
  driver?: string;
  min_km?: number;
  has_event?: string;
}

export function useTrips(p: TripsQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: activityKeys.trips(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/trips', { params: { query: { ...p, cursor: pageParam, limit: 200 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useTrip(id: string | undefined) {
  return useQuery({
    queryKey: activityKeys.trip(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/trips/{trip_id}', { params: { path: { trip_id: id ?? '' } } })),
    enabled: !!id,
  });
}

export interface DetectionsQuery extends Window {
  project?: string[];
  group?: string[];
  vehicle?: string;
  driver?: string;
  type?: string[];
  severity?: string[];
  review?: string[];
  has_video?: boolean;
}

export function useDetections(p: DetectionsQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: activityKeys.detections(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/detections', { params: { query: { ...p, cursor: pageParam, limit: 200 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** Counts for the same filters (chips, KPI pills). */
export function useDetectionSummary(p: DetectionsQuery, enabled = true) {
  return useQuery({
    queryKey: activityKeys.summary(p),
    queryFn: () => unwrap(api.GET('/api/detections/summary', { params: { query: p } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useDetection(id: string | undefined) {
  return useQuery({
    queryKey: activityKeys.detection(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/detections/{detection_id}', { params: { path: { detection_id: id ?? '' } } })),
    enabled: !!id,
  });
}

/** The score tree (structure only; labels are the web's). Stable for a deployment. */
export function useScoreTree() {
  return useQuery({
    queryKey: activityKeys.scoreTree,
    queryFn: () => unwrap(api.GET('/api/telemetry/score-tree')),
    staleTime: Infinity,
  });
}

/** Confirm or dismiss an event (action `trips.review_detection`). */
export function useReviewDetection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; review: 'confirmed' | 'dismissed'; note?: string }) =>
      unwrap(api.POST('/api/detections/{detection_id}/review', { params: { path: { detection_id: v.id } }, body: { review: v.review, note: v.note ?? '' } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: activityKeys.all }),
  });
}

/** Mark a trip business or private (action `trips.set_category`). */
export function useSetTripCategory(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (category: 'business' | 'private') =>
      unwrap(api.POST('/api/trips/{trip_id}/category', { params: { path: { trip_id: id } }, body: { category } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: activityKeys.all }),
  });
}
